// src/pages/expense/_shared/ExpenseDraftDetailModal.jsx
// ✅ 전 사업 확산 [1] 공통화 — 환경/종합 파일럿의 상세 모달(ExpenseEnvTotalDraftDetailModal.jsx)
// 을 그대로 일반화한 것. 기안이 있는 건은 GET /draft/documents/{id}/를 불러와 작성
// 페이지와 동일한 buildPreviewValues로 values를 재구성하고, 기존 ExpenseProposalPreview를
// 그대로 재사용해 렌더한다. 기안이 없는 건(구 페이지로 입력)은 지출 데이터 필드만
// 읽기전용으로 보여준다. 사업별 차이(삭제/조회 API 경로, 구분·내용·분야 필드명)는
// config prop으로 받는다.
import React, { useEffect, useState } from "react";
import useBackdropClose from "../../../hooks/useBackdropClose";
import api from "../../../api";
import ExpenseProposalPreview from "../../../components/preview/expense/ExpenseProposalPreview";
import { buildPreviewValues, formatFileSize } from "./ExpenseDraftNew";

const STATUS_LABEL = { draft: "작성중", pending: "결재중", approved: "완료", rejected: "반려" };
// skipped(생략) = 결재 단계를 승인 없이 종결한 것(예: 퇴임). 회색 — 승인(초록)과 섞이지 않게.
const RESULT_LABEL = { pending: "결재대기", approved: "승인", rejected: "반려", skipped: "생략" };
const RESULT_COLOR = { pending: "#666", approved: "#0b7a3b", rejected: "#c0392b", skipped: "#7f8c8d" };

export default function ExpenseDraftDetailModal({ config, row, onClose, onDeleted }) {
  const backdrop = useBackdropClose(onClose);

  const { dataApiBase, fieldNameMap } = config;
  const [draftDoc, setDraftDoc] = useState(null);
  const [loading, setLoading] = useState(!!row.draft_document_id);
  const [deleting, setDeleting] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const hasDraft = !!row.draft_document_id;
  const approved = hasDraft && row.draft_status === "approved";

  async function loadDraft() {
    if (!hasDraft) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/draft/documents/${row.draft_document_id}/`);
      setDraftDoc(data);
    } catch (err) {
      console.error("❌ 기안 상세 불러오기 실패:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.draft_document_id]);

  async function handleUpload(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0 || !row.draft_document_id) return;
    setUploading(true);
    setUploadError("");
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("document", row.draft_document_id);
        formData.append("file", file);
        await api.post("/draft/attachments/", formData);
      }
      await loadDraft();
    } catch (err) {
      console.error("❌ 첨부 추가 업로드 실패:", err.response?.data || err);
      setUploadError(
        err.response?.data?.file?.[0] || "업로드에 실패했습니다. (허용 파일: pdf/hwp/office 문서/이미지/zip 등)"
      );
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete() {
    if (approved) {
      alert("결재완료 건은 삭제할 수 없습니다. (정책 확정 전까지 비활성화)");
      return;
    }
    const msg = hasDraft
      ? `기안(${row.draft_no})과 연결된 지출 데이터를 함께 삭제합니다.\n\n계속하시겠습니까?`
      : `삭제하시겠습니까?\n\n기안번호: ${row.draft_no}\n\n삭제 후 복구할 수 없습니다.`;
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      if (hasDraft) {
        await api.delete(`/draft/documents/${row.draft_document_id}/`);
      } else {
        await api.delete(`${dataApiBase}${row.id}/`);
      }
      onDeleted?.();
      onClose();
    } catch (err) {
      console.error(err);
      const msg2 = err.response?.data ? JSON.stringify(err.response.data).slice(0, 200) : err.message;
      alert(`삭제 실패: ${msg2}`);
    } finally {
      setDeleting(false);
    }
  }

  const previewValues = draftDoc
    ? buildPreviewValues(draftDoc.template_code, {
        title: draftDoc.title,
        attachments: draftDoc.attachment_items || [],
        itemList: row.items || [],
        salaryList: row.salaries || [],
        genericTableRows: draftDoc.template_data?.__genericTableRows || [],
        extraFields: draftDoc.template_data || {},
        partnerList: row.partners || [],
        totalAmount: Number(draftDoc.amount) || 0,
      })
    : null;

  const approvalLineForPreview = (draftDoc?.approval_lines || []).map((l) => ({
    id: l.approver,
    position: l.position,
    name: l.approver_name,
    status: l.approval_type,
  }));

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
      {...backdrop}
    >
      <div
        style={{ background: "#fff", borderRadius: 8, padding: 20, width: 960, maxHeight: "88vh", overflowY: "auto" }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{row.title || "(제목 없음)"} — {row.draft_no}</h3>
          <button onClick={onClose} style={{ width: 28, height: 28, color: "#222" }}>✕</button>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
          <span style={{ fontSize: 13, color: "#666" }}>결재 상태:</span>
          <strong>{hasDraft ? STATUS_LABEL[row.draft_status] || row.draft_status : "기안없음(구 페이지 입력)"}</strong>
          <button
            onClick={handleDelete}
            disabled={deleting || approved}
            title={approved ? "결재완료 건은 삭제할 수 없습니다" : undefined}
            style={{ marginLeft: "auto", padding: "6px 14px", color: "#222" }}
          >
            {deleting ? "삭제 중…" : "기안 삭제"}
          </button>
        </div>

        {!hasDraft && (
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 16 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <tbody>
                <tr><td style={{ padding: 4, color: "#666", width: 140 }}>작성일</td><td>{row.execution_date}</td></tr>
                <tr><td style={{ padding: 4, color: "#666" }}>거래처</td><td>{row.partners?.length ? row.partners.map((p) => p.name).join(", ") : row.partner_name || "-"}</td></tr>
                <tr><td style={{ padding: 4, color: "#666" }}>사용내역 구분/내용/분야</td><td>{row[fieldNameMap.categoryName]} / {row[fieldNameMap.itemName]} / {row[fieldNameMap.fieldName]}</td></tr>
                <tr><td style={{ padding: 4, color: "#666" }}>공급가액/부가세/합계</td><td>{Number(row.supply_amount || 0).toLocaleString()} / {Number(row.vat || 0).toLocaleString()} / {Number(row.total_amount || 0).toLocaleString()}</td></tr>
                <tr><td style={{ padding: 4, color: "#666" }}>비고</td><td>{row.remark || "-"}</td></tr>
              </tbody>
            </table>
          </div>
        )}

        {hasDraft && loading && <div>불러오는 중…</div>}

        {hasDraft && !loading && draftDoc && (
          <>
            <div style={{ display: "flex", gap: 20 }}>
              <div style={{ flex: "1 1 480px", border: "1px solid #e5e7eb", borderRadius: 8, padding: 12 }}>
                {draftDoc.template_code ? (
                  <ExpenseProposalPreview
                    templateCode={draftDoc.template_code}
                    values={previewValues}
                    documentNumber={draftDoc.draft_no}
                    approvalLine={approvalLineForPreview}
                  />
                ) : (
                  <div style={{ fontSize: 14 }}>
                    <p style={{ color: "#666" }}>"기타(일반 폼)"으로 작성된 문서라 정형 미리보기가 없습니다.</p>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <tbody>
                        <tr><td style={{ padding: 4, color: "#666", width: 100 }}>제목</td><td>{draftDoc.title}</td></tr>
                        <tr><td style={{ padding: 4, color: "#666" }}>금액</td><td>{Number(draftDoc.amount || 0).toLocaleString()}원</td></tr>
                        <tr><td style={{ padding: 4, color: "#666" }}>내용</td><td style={{ whiteSpace: "pre-line" }}>{draftDoc.desc}</td></tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div style={{ flex: "1 1 360px", display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <strong>결재선</strong>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 6 }}>
                    <thead>
                      <tr style={{ color: "#666" }}>
                        <th style={{ textAlign: "left", padding: 4 }}>순번</th>
                        <th style={{ textAlign: "left", padding: 4 }}>직책</th>
                        <th style={{ textAlign: "left", padding: 4 }}>이름</th>
                        <th style={{ textAlign: "left", padding: 4 }}>구분</th>
                        <th style={{ textAlign: "left", padding: 4 }}>결과</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(draftDoc.approval_lines || []).map((l) => (
                        <tr key={l.id}>
                          <td style={{ padding: 4 }}>{l.order}</td>
                          <td style={{ padding: 4 }}>{l.position}</td>
                          <td style={{ padding: 4 }}>{l.approver_name}</td>
                          <td style={{ padding: 4 }}>{l.approval_type}</td>
                          <td style={{ padding: 4, color: RESULT_COLOR[l.result] || "#666" }}>
                            {RESULT_LABEL[l.result] || l.result}
                            {l.comment ? ` (${l.comment})` : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <strong>첨부파일</strong>
                    <label style={{ padding: "4px 12px", fontSize: 13, border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", color: "#222" }}>
                      {uploading ? "업로드 중…" : "+ 추가 업로드"}
                      <input type="file" multiple onChange={handleUpload} disabled={uploading} style={{ display: "none" }} />
                    </label>
                    <span style={{ fontSize: 12, color: "#999" }}>(반려 후 보완 등 보조 경로)</span>
                  </div>
                  {uploadError && <div style={{ color: "#c0392b", fontSize: 12, marginTop: 4 }}>{uploadError}</div>}
                  {(draftDoc.attachments || []).length === 0 ? (
                    <div style={{ color: "#999", fontSize: 13, marginTop: 6 }}>첨부된 파일이 없습니다.</div>
                  ) : (
                    <ul style={{ listStyle: "none", padding: 0, marginTop: 6 }}>
                      {draftDoc.attachments.map((a) => (
                        <li key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 13 }}>
                          <a href={a.file} download={a.original_filename} target="_blank" rel="noreferrer">
                            📎 {a.original_filename}
                          </a>
                          <span style={{ color: "#999" }}>{a.file_size_display || formatFileSize(a.file_size)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
